import { FC } from 'react';
import classNames from 'classnames';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { Button } from '@/Components/Button';
import { WhatsappSvg } from '@/Components/Icons/WhatsappSvg';
import { MailSvg } from '@/Components/Icons/MailSvg';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';
import { QuestionFruitSvg } from '@/Components/Icons/QuestionFruitSvg';
import { ON_TRIAL_TEAM_STATUS, TeamStatus } from '@/types/team.type';
import { useDataState } from '@/hook/useDataState';
import { useToast } from '@/hook/useToast';
import {
    INITIAL_DATA_STATE,
    LOADING_DATA_STATE,
    SUCCESS_DATA_STATE,
} from '@/types/data.type';
import { CheckSvg } from '@/Components/Icons/CheckSvg';
import { RotateSvg } from '@/Components/Icons/RotateSvg';

export type TrialExtendModalProps = AppModalProps<{
    teamStatus: TeamStatus;
    daysLeft: number;
    userId: string;
    workspaceName: string;
    workspaceId: string;
    forTesting: boolean;
    subscriptionBillingAndOrganisationManagement: boolean;
}>;

export const TrialExtendModal: FC<TrialExtendModalProps> = ({
    payload,
    onClose,
}) => {
    const isOnTrial = payload?.teamStatus === ON_TRIAL_TEAM_STATUS;

    const { showToast } = useToast();

    const {
        dataState: speakToTeamDataState,
        setLoading: setSpeakToTeamLoading,
        setSuccess: setSpeakToTeamSuccess,
        setInitial: setSpeakToTeamInitial,
    } = useDataState();

    const {
        dataState: requestUpgradeDataState,
        setLoading: setRequestUpgradeLoading,
        setSuccess: setRequestUpgradeSuccess,
        setInitial: setRequestUpgradeInitial,
    } = useDataState();

    const onRequestUpgrade = (isRequestUpgrade: boolean) => {
        if (!payload?.userId || !payload?.workspaceName) {
            return;
        }

        if (
            (isRequestUpgrade &&
                requestUpgradeDataState === SUCCESS_DATA_STATE) ||
            (!isRequestUpgrade && speakToTeamDataState === SUCCESS_DATA_STATE)
        ) {
            return;
        }

        const reqPayload = {
            userId: payload?.userId,
            origin: window.location.origin,
            workspaceId: payload?.workspaceId,
            workspaceName: payload?.workspaceName,
            for_testing: payload?.forTesting,
            buttonLabel: isRequestUpgrade
                ? 'Request upgrade'
                : 'Speak to the team',
        };

        if (isRequestUpgrade) {
            setRequestUpgradeLoading();
        } else {
            setSpeakToTeamLoading();
        }

        // NOTE: For testing purposes only!
        // setTimeout(() => {
        //     if (isRequestUpgrade) {
        //         setRequestUpgradeSuccess();
        //     } else {
        //         setSpeakToTeamSuccess();
        //     }
        // }, 3000);

        fetch(`https://hooks.zapier.com/hooks/catch/18870477/2per88a/`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            body: JSON.stringify(reqPayload),
        })
            .then(function () {
                setTimeout(() => {
                    if (isRequestUpgrade) {
                        setRequestUpgradeSuccess();
                    } else {
                        setSpeakToTeamSuccess();
                    }
                }, 500);
            })
            .catch(function (ex) {
                setTimeout(() => {
                    if (isRequestUpgrade) {
                        setRequestUpgradeInitial();
                    } else {
                        setSpeakToTeamInitial();
                    }
                }, 500);

                showToast({
                    variant: 'danger',
                    label: `Something went wrong`,
                });
            });
    };

    function getContentTextBlocks(): [string, string] {
        const textBlocks: [string, string] = ['', ''];

        if (isOnTrial) {
            textBlocks[0] = `You have ${payload?.daysLeft || 0} days to try out ReelFlow!`;

            textBlocks[1] = `After this trial, you’ll need to upgrade to continue showing the videos on your website. `;
        } else {
            textBlocks[1] = `To keep driving engagement with interactive videos on your website, you’ll need to upgrade your plan. `;

            if (payload.subscriptionBillingAndOrganisationManagement) {
                textBlocks[0] = '';
            } else {
                textBlocks[0] = `Your ReelFlow trial has ended.`;
            }
        }

        if (!payload.subscriptionBillingAndOrganisationManagement) {
            textBlocks[1] += `Here are your options:`;
        }

        return textBlocks;
    }

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            contentContainerClassName="py-4 text-brand-ocean flex flex-col justify-center items-center">
            <div className="flex flex-row items-center w-full px-6">
                <div className="flex-1 text-heading-four !font-bold text-brand-ocean text-left">
                    {isOnTrial
                        ? 'Your ReelFlow trial'
                        : 'Your ReelFlow trial has ended'}
                </div>
                <button
                    className="p-2 -mr-1 transition-all rounded-md hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            <div className="mt-2 mb-4 relative flex flex-row items-center justify-center w-full gap-x-2">
                <FlickAnimation className="mr-12 z-10" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ">
                    {isOnTrial ? (
                        <FruitBlueberrySvg
                            width={54}
                            height={54}
                            className="ml-8 mt-8"
                        />
                    ) : (
                        <QuestionFruitSvg
                            width={54}
                            height={54}
                            className="ml-10 mt-2"
                        />
                    )}
                </div>
            </div>
            <div className="flex flex-col mt-2 mx-6">
                <div className="text-body-text-15 text-brand-ocean">
                    {getContentTextBlocks()[0]}
                </div>
                <div className="text-body-text-15 mt-4 text-brand-ocean">
                    {getContentTextBlocks()[1]}
                </div>
                <div className="mt-6 flex flex-col gap-y-2">
                    {!payload.subscriptionBillingAndOrganisationManagement ? (
                        <>
                            <Button
                                variant="default"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1"
                                elementType="a"
                                href="https://rflw.co/guide-to-plans-and-pricing"
                                target="_blank">
                                <MailSvg className="w-4 h-4" />
                                View plans (in new tab)
                            </Button>
                            <Button
                                variant="default"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1 relative"
                                onClick={() => onRequestUpgrade(false)}>
                                <div
                                    key={INITIAL_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                speakToTeamDataState ===
                                                INITIAL_DATA_STATE,
                                        },
                                    )}>
                                    <WhatsappSvg className="w-4 h-4" />
                                    Speak to the team
                                </div>
                                <div
                                    key={LOADING_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                speakToTeamDataState ===
                                                LOADING_DATA_STATE,
                                        },
                                    )}>
                                    <div className="animate-spin">
                                        <RotateSvg className="[&_path]:!stroke-brand-ocean" />
                                    </div>
                                    Sending…
                                </div>
                                <div
                                    key={SUCCESS_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                speakToTeamDataState ===
                                                SUCCESS_DATA_STATE,
                                        },
                                    )}>
                                    <CheckSvg className="[&_path]:!fill-brand-ocean" />
                                    Thanks, we’ll be in touch shortly!
                                </div>
                            </Button>
                            <Button
                                variant="primary"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1 relative"
                                onClick={() => onRequestUpgrade(true)}>
                                <div
                                    key={INITIAL_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                requestUpgradeDataState ===
                                                INITIAL_DATA_STATE,
                                        },
                                    )}>
                                    Request upgrade…
                                </div>
                                <div
                                    key={LOADING_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                requestUpgradeDataState ===
                                                LOADING_DATA_STATE,
                                        },
                                    )}>
                                    <div className="animate-spin">
                                        <RotateSvg className="[&_path]:!stroke-white" />
                                    </div>
                                    Requesting…
                                </div>
                                <div
                                    key={SUCCESS_DATA_STATE}
                                    className={classNames(
                                        'absolute top-0 left-0 w-full h-full flex flex-row items-center justify-center gap-x-1 opacity-0 transition-opacity duration-200',
                                        {
                                            'opacity-100':
                                                requestUpgradeDataState ===
                                                SUCCESS_DATA_STATE,
                                        },
                                    )}>
                                    <CheckSvg className="[&_path]:!fill-white" />
                                    Thanks, we’ll be in touch shortly!
                                </div>
                            </Button>
                        </>
                    ) : (
                        <Button
                            elementType="link"
                            // TODO: Use "route" once route is added in Ziggy
                            href={'/dashboard/settings/billing'}
                            variant="primary"
                            className="w-full h-9 flex flex-row items-center justify-center gap-x-1 relative"
                            onClick={() => onClose()}>
                            View Plans & Upgrade
                        </Button>
                    )}
                </div>
            </div>
        </ModalView>
    );
};
